import { pb } from './pocketbase';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

export const searchCompanies = async (query) => {
    try {
        if (!query || query.length < 2) {
            return [];
        }

        // Recherche par nom ou SIRET avec expansion des tuteurs
        const result = await pb.collection('companies').getList(1, 10, {
            filter: `name ~ "${query}" || siret ~ "${query}"`,
            expand: 'tutor_id',
        });

        return result.items;
    } catch (error) {
        console.error('Erreur lors de la recherche des entreprises:', error);
        return [];
    }
};

export const getCompanies = async (options = {}) => {
    try {
        const queryOptions = {
            sort: options.sort || '-created',
            page: options.page || 1,
            perPage: options.perPage || 20,
            filter: options.filter || null,
            fullData: options.fullData || false
        };
        
        const pbQuery = {
            sort: queryOptions.sort
        };

        // Si fullData est false, on limite les champs récupérés pour optimiser
        // Si fullData est true, on récupère tous les champs pour la page de détail
        if (!queryOptions.fullData) {
            pbQuery.fields = options.fields || 'id,name,siret,sector,status,email,phone,type_of_company,employee_count,contacts,recruitmentPeriod';
        }

        if (queryOptions.filter) {
            pbQuery.filter = queryOptions.filter;
        }

        const result = await pb.collection('companies').getList(
            queryOptions.page,
            queryOptions.perPage,
            pbQuery // Utiliser l'objet pbQuery qui contient sort, fields et potentiellement filter
        );
        
        return {
            items: result.items,
            totalItems: result.totalItems,
            totalPages: result.totalPages,
            page: result.page
        };
    } catch (error) {
        console.error('Erreur lors de la récupération des entreprises:', error);
        return {
            items: [],
            totalItems: 0,
            totalPages: 0,
            page: 1
        };
    }
};

export const getCompanyById = async (id) => {
    try {
        const company = await pb.collection('companies').getOne(id);
        return company;
    } catch (error) {
        console.error(`Erreur lors de la récupération de l'entreprise ${id}:`, error);
        return null;
    }
};

export const createCompany = async (companyData) => {
    try {
        // Appeler l'API du serveur pour créer un étudiant
        const response = await fetch(`${API_BASE_URL}/api/createCompany`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': `Bearer ${JSON.parse(localStorage.getItem('pocketbase_auth')).token}`
            },
            body: JSON.stringify(companyData)
        });

        const responseData = await response.json();

        if (!response.ok) {
            throw new Error(responseData.error || 'Une erreur inattendue est survenue lors de la création.');
        }

        return responseData;
    } catch (error) {
        console.error('Erreur lors de la création de l\'entreprise:', error);
        if (error.message.includes('Failed to fetch')) {
            throw new Error('Erreur de connexion au serveur. Veuillez réessayer.');
        }
        throw error;
    }
};

export const updateCompany = async (id, companyData) => {
    try {
        if (pb.authStore.isValid && pb.authStore.model) {
            const updatedCompany = await pb.collection('companies').update(id, companyData);
            return updatedCompany;
        } else {
            console.error('Aucun utilisateur authentifié. Impossible de mettre à jour l\'entreprise sans partner_id.');
            return null;
        }    
    } catch (error) {
        console.error(`Erreur lors de la mise à jour de l'entreprise ${id}:`, error);
        throw error;
    }
};

export const deleteCompany = async (id) => {
    try {
        // 1. Récupérer tous les utilisateurs associés à cette entreprise
        const usersResult = await pb.collection('users').getList(1, 100, {
            filter: `company_id = "${id}"`
        });
        
        // 2. Supprimer chaque utilisateur associé
        const userDeletionPromises = usersResult.items.map(user => 
            pb.collection('users').delete(user.id)
        );
        
        // Attendre que toutes les suppressions d'utilisateurs soient terminées
        await Promise.all(userDeletionPromises);
        
        // 3. Supprimer l'entreprise
        await pb.collection('companies').delete(id);
        
        return true;
    } catch (error) {
        console.error(`Erreur lors de la suppression de l'entreprise ${id} ou de ses utilisateurs:`, error);
        throw error;
    }
};

export const subscribeToCompanyContracts = (companyId, onContractChange) => {
    return pb.collection('contracts').subscribe('*', (e) => {
        if (e.record && e.record.company_id === companyId) {
            if (typeof onContractChange === 'function') {
                onContractChange(e);
            }
        }
    });
};

export const unsubscribeFromContracts = (subscriptionId) => {
    if (subscriptionId) {
        pb.collection('contracts').unsubscribe(subscriptionId);
    }
};

export const getCompanyForCurrentUser = async () => {
    try {
        if (!pb.authStore.isValid || !pb.authStore.model) {
            throw new Error('Utilisateur non authentifié');
        }

        const currentUser = pb.authStore.model;
        
        // Si l'utilisateur a un company_id, récupérer l'entreprise directement
        if (currentUser.company_id) {
            const company = await pb.collection('companies').getOne(currentUser.company_id);
            return company;
        }
        
        // Sinon, chercher l'entreprise via user_ref
        const companiesResult = await pb.collection('companies').getList(1, 1, {
            filter: `user_ref = "${currentUser.id}"`
        });
        
        if (companiesResult.items.length > 0) {
            return companiesResult.items[0];
        }
        
        return null;
    } catch (error) {
        console.error('Erreur lors de la récupération de l\'entreprise pour l\'utilisateur connecté:', error);
        throw error;
    }
};

export const sendCompanyCredentials = async (companyId) => {
    try {
        // Récupérer l'entreprise pour obtenir son director_email
        const company = await pb.collection('companies').getOne(companyId);
        
        if (!company || !company.director_email) {
            throw new Error('Email du directeur de l\'entreprise non trouvé');
        }
        
        // Utiliser la même mécanique que RequestPasswordReset mais avec l'email du directeur
        await pb.collection('users').requestPasswordReset(company.director_email, {
            emailTemplate: 'password_reset',
            // URL de réinitialisation pointant vers le frontend
            actionUrl: `${window.location.origin}/reset-password?token={TOKEN}`
        });
        
        return { success: true, message: 'Email de réinitialisation envoyé avec succès' };
    } catch (error) {
        console.error('Erreur lors de l\'envoi des identifiants:', error);
        throw error;
    }
};
